// Analytics utility for tracking user interactions with Umami
// Privacy-focused: only tracks aggregate behavioral data, no personal information

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, data?: Record<string, any>) => void;
    };
  }
}

export interface AnalyticsEvent {
  action: string;
  category?: string;
  value?: string | number;
  source?: string;
}

// Check if user has opted out of analytics
export const getAnalyticsConsent = (): boolean => {
  try {
    return localStorage.getItem('analytics-consent') !== 'false';
  } catch {
    return true; // Default to consented if localStorage unavailable
  }
};

export const setAnalyticsConsent = (consented: boolean): void => {
  try {
    localStorage.setItem('analytics-consent', consented.toString());
  } catch {
    // Silently fail if localStorage unavailable
  }
};

// Track custom events with privacy checks
export const trackEvent = (event: AnalyticsEvent): void => {
  if (!getAnalyticsConsent()) {
    return; // Respect user privacy choice
  }

  if (typeof window !== 'undefined' && window.umami) {
    try {
      window.umami.track(event.action, {
        category: event.category,
        value: event.value,
        source: event.source,
      });
    } catch (error) {
      // Silently fail if tracking fails - don't break the app
      console.warn('[Analytics] Tracking failed:', error);
    }
  }
};

// Specific tracking functions for mesh simulator interactions
export const trackMeshControl = (control: string, value: string | number): void => {
  trackEvent({
    action: 'mesh_control',
    category: 'simulator',
    value: typeof value === 'string' ? value : value.toString(),
    source: control,
  });
};

export const trackTopologyMode = (mode: 'knn' | 'radius'): void => {
  trackEvent({
    action: 'topology_mode_change',
    category: 'simulator',
    value: mode,
  });
};

export const trackPausePlay = (action: 'pause' | 'play'): void => {
  trackEvent({
    action: 'simulation_toggle',
    category: 'simulator',
    value: action,
  });
};

export const trackPlaygroundMode = (action: 'enter' | 'exit'): void => {
  trackEvent({
    action: 'playground_mode',
    category: 'navigation',
    value: action,
  });
};

export const trackExternalLink = (destination: string, source?: string): void => {
  trackEvent({
    action: 'external_link',
    category: 'conversion',
    value: destination,
    source: source,
  });
};
